// Module E · Recommended public-site page templates.
//
// Calm, useful starter pages a director can publish in one click. Each
// template defines a slug, title, kind (hint for SEO/structured data),
// and a markdown body that uses the same `## Heading` convention the
// public Home picks up for the auto-detected Programs section.

export const RECOMMENDED_PAGE_TEMPLATES = [
  {
    key: "programs",
    slug: "programs",
    title: "Programs",
    kind: "programs",
    description: "Age groups, ratios, daily rhythm. Used by the homepage to render program cards.",
    icon: "ChalkboardTeacher",
    body_md: `# Our programs

We keep classrooms small so every child is known by name. Below is what a typical day looks like for each age group.

## Infants (6 weeks – 12 months)
A calm, predictable space with on-demand feeding, plenty of floor time, and lots of one-on-one connection. Ratio: 1 teacher per 4 babies. Hours: 7:30 a.m.–5:30 p.m.

## Toddlers (12 months – 2 years)
Walking, climbing, first words, first friendships. We use simple sign language and lots of outdoor time. Ratio: 1 teacher per 5 toddlers. Hours: 7:30 a.m.–5:30 p.m.

## Preschoolers (3 – 5 years)
Project-based play, group story time, and gentle introductions to letters, numbers, and emotions. Ratio: 1 teacher per 8 preschoolers. Hours: 7:30 a.m.–5:30 p.m.

## What every classroom shares
- Outdoor time twice a day, weather permitting
- Family-style meals prepared in-house
- Daily report sent to parents through the app
- Staff trained in CPR and first aid
`,
  },
  {
    key: "tuition",
    slug: "tuition",
    title: "Tuition",
    kind: "tuition",
    description: "Calm pricing breakdown + 'what's included'. Auto-emits Service structured data.",
    icon: "CurrencyCircleDollar",
    body_md: `# Tuition

We try to keep tuition simple — one monthly amount, billed at the start of the month, with autopay supported. There are no surprise fees.

## Monthly tuition
- **Infants (full-time)**: starts at $1,995/month
- **Toddlers (full-time)**: starts at $1,795/month
- **Preschool (full-time)**: starts at $1,595/month
- **Part-time options**: 3 or 5 half-days per week — please ask

## What's included
- All meals and snacks
- Diapers, wipes, and bottle care for infants
- All curriculum materials and field-trip costs
- Daily reports through the parent app

## What's not included
- A one-time $150 enrollment fee
- A refundable security deposit equal to two weeks of tuition

We also offer a sibling discount on the second child. Please reach out — we'd love to walk you through the full breakdown.
`,
  },
  {
    key: "faq",
    slug: "faq",
    title: "Frequently asked questions",
    kind: "faq",
    description: "Q/A pairs auto-emit FAQPage structured data for richer Google results.",
    icon: "Question",
    body_md: `# Frequently asked questions

## What hours are you open?
We're open Monday through Friday, 7:30 a.m. to 5:30 p.m. We're closed for major federal holidays and a one-week summer break, communicated annually.

## How do drop-off and pick-up work?
You'll have a four-digit pick-up PIN that any authorized adult can use. We log every entry and exit, and parents can see the audit trail in the app.

## What's your sick policy?
Children with a fever, vomiting, or contagious symptoms are asked to stay home for 24 hours after the last symptom. We follow CDC guidance on common illnesses.

## How do I see what my child did today?
Every classroom posts a daily report through the app — meals, naps, diapers/bathroom, milestones, and a photo or two. You'll get a summary every evening.

## How do I pay tuition?
Tuition is billed monthly and you can pay in the parent app. We support autopay with any major card, and you can set it up in Billing → Autopay.

## What if my child has allergies or special dietary needs?
We work with each family to plan around allergies and dietary restrictions. Please let us know on the enrollment form, and we'll talk through the details together.
`,
  },
  {
    key: "team",
    slug: "team",
    title: "Our team",
    kind: "team",
    description: "Director and lead-teacher introductions. Calm bios.",
    icon: "Users",
    body_md: `# Our team

Every teacher here is someone we'd send our own kids to. Each has CPR, first aid, and ongoing early-childhood training.

## A note from the director
Hi, I'm the director. I've been working with young children for over a decade and opened this center because I wanted a place that felt calm to parents and predictable to kids — somewhere a child can settle in without the noise of a giant facility.

## Lead teachers
Our lead teachers stay in their classrooms for the long haul. We believe consistent caregivers are the heart of good early childhood care, so we invest in keeping our team for years, not months.

## Want to meet us?
Contact us to schedule a tour. We'll spend 30 minutes together — half walking the classrooms, half talking through your family's needs.
`,
  },
];

/** Produce a JS-safe page payload from a template, ready to pass into createPage(). */
export function buildPageFromTemplate(template) {
  return {
    slug: template.slug,
    title: template.title,
    bodyMd: template.body_md,
    status: "draft",
  };
}
